import React from "react";
import { useTranslation } from "react-i18next";
import Auth from "./Auth.tsx";
import LanguageSwitcher from "./LanguageSwitcher.tsx";
import type { Profile, User } from "../types/index.ts";
import Wordmark from "./Wordmark.tsx";

interface AppHeaderProps {
  user: User | null;
  profile: Profile | null;
  onLogin: (_user: User) => void;
  onLogout: () => void;
  // Click handler for the Hi-pill — opens the profile-name modal.
  onEditName?: () => void;
  // Click handler for the logo + title. When set, they become a clickable
  // "Home" affordance — used on Dashboard / Detail / main app to let the
  // user revisit the marketing landing (rules, scoring info, etc.) without
  // having to log out.
  onHomeClick?: () => void;
  // Optional content rendered between the title and the auth pill. Used by
  // the main app to slot in the round-switcher + league selector. Dashboard
  // and Detail pages leave this empty.
  middleSlot?: React.ReactNode;
}

/**
 * Shared header used across Dashboard, TipRoundDetail, and the main tipping
 * view. Always renders: logo + title + (optional middle controls) + auth
 * pill + language switcher.
 */
const AppHeader: React.FC<AppHeaderProps> = ({
  user,
  profile,
  onLogin,
  onLogout,
  onEditName,
  onHomeClick,
  middleSlot,
}) => {
  const { t } = useTranslation();
  // No size prop here — the default 4rem lives in the .wordmark CSS class so
  // the mobile media query (src/index.css) can override it responsively.
  // An inline font-size would win specificity and break that breakpoint.
  const titleContent = (
    <h1 style={{ margin: 0 }}>
      <Wordmark />
    </h1>
  );

  return (
    <header className="app-header">
      <div className="header-content">
        {onHomeClick ? (
          <button
            type="button"
            onClick={onHomeClick}
            className="header-title"
            title={t("appHeader.homeTitle")}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            {titleContent}
          </button>
        ) : (
          <div className="header-title">{titleContent}</div>
        )}
        <div className="header-controls">
          {middleSlot}
          <Auth
            user={user}
            profile={profile}
            onLogin={onLogin}
            onLogout={onLogout}
            onEditName={onEditName}
          />
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
